import React, { createContext, useState, useContext, useEffect } from 'react';
import envVars from 'base/shared/Env';
import fetch from 'base/shared/fetch-data';
import { getAPIUrl as getServerUrl } from 'base/shared/Utils';
import { useSelector } from 'react-redux';

// Create a Context for RentCalculator
const RentCalculatorContext = createContext();

// Custom hook to use the RentCalculator context
export const useRentCalculator = () => {
  return useContext(RentCalculatorContext);
};

// RentCalculator Provider to wrap around components that need access to the context
export const RentCalculatorProvider = ({ children }) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFeedback, setFeedback] = useState(false);

  const token = useSelector((state) => state.Auth.access_token);
  const [cityData, setCityData] = useState(null);

  const submitRentCalculation = async (payload) => {
    setLoading(true);
    setError(null);

    // Test cases para desarrollo
    if (payload.email === 'linda@badiapp.com') {
      console.log('Payload for linda@badiapp.com', payload);
      setLoading(false);
      setFeedback(true);
      return;
    }

    // Test case para error
    if (payload.email === 'error@test.com') {
      setError('Error de prueba');
      setLoading(false);
      setFeedback(true);
      return;
    }

    const { BADI_CALCULATE_RENT_RESULT } = envVars();
    const serviceUrl = getServerUrl(BADI_CALCULATE_RENT_RESULT);

    try {
      return fetch(serviceUrl, {
        method: 'POST',
        token,
        params: payload,
      }).then(({ data }) => {
        return data;
      });
    } catch (error) {
      console.error('Error submitting calculation:', error);
      setError(error);
    } finally {
      setFeedback(true);
      setLoading(false);
    }
  };

  const fetchRentCalculatorCities = async (token) => {
    setLoading(true);
    setError(null);

    const { BADI_CALCULATE_RENT_CITIES } = envVars();
    const serviceUrl = getServerUrl(BADI_CALCULATE_RENT_CITIES);

    try {
      const response = await fetch(serviceUrl, { token });

      setCities(response.data);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCityData = (selectedCity) => {
    if (!selectedCity) return null;

    // Find the city object that matches the selected city name (e.g., 'barcelona')
    const city = cities.find((cityObj) => cityObj[selectedCity]);

    // If found, return the relevant city data
    return city ? city[selectedCity] : null;
  };

  useEffect(() => {
    fetchRentCalculatorCities(token);
  }, [token]);

  return (
    <RentCalculatorContext.Provider
      value={{
        cities,
        loading,
        error,
        cityData,
        isFeedback,
        fetchRentCalculatorCities,
        getCityData,
        setCityData,
        submitRentCalculation,
      }}
    >
      {children}
    </RentCalculatorContext.Provider>
  );
};
